import { TitleCasePipe } from '@angular/common';
import { LocalDate, LocalDateTime, Month, TemporalAdjusters } from 'js-joda';
import { BehaviorSubject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common";
function memoize(func) {
    const cache = new Map();
    return function (...args) {
        const key = JSON.stringify(args);
        if (!cache.has(key)) {
            cache.set(key, func.apply(this, args));
        }
        return cache.get(key);
    };
}
export class DateService {
    constructor(titleFormatter) {
        this.titleFormatter = titleFormatter;
        this.now$ = new BehaviorSubject(LocalDate.now());
        this.yearsWithTimestamp$ = this.now$.pipe(map(now => this.memoizedGetYearsWithTimestamp(now)), shareReplay(1));
        this.monthsWithTimestamp$ = this.now$.pipe(map(now => this.memoizedGetMonthsWithTimestamp(now)), shareReplay(1));
        this.memoizedGetYearTimestamp = memoize((date) => {
            const year = date.year().toString();
            const startMonth = date.withMonth(Month.JANUARY);
            const startTimestamp = this.toLocalDateTime(startMonth.with(TemporalAdjusters.firstDayOfMonth()));
            const endMonth = date.withMonth(Month.DECEMBER);
            const endTimestamp = this.toLocalDateTime(endMonth.with(TemporalAdjusters.lastDayOfMonth()))
                .plusDays(1)
                .minusSeconds(1);
            return { year, startMonth, startTimestamp, endMonth, endTimestamp };
        });
        this.memoizedGetMonthYearTimestamp = memoize((date) => {
            const firstDayofMonth = date.with(TemporalAdjusters.firstDayOfMonth());
            const lastDayOfMonth = date.with(TemporalAdjusters.lastDayOfMonth());
            const startTimestamp = this.toLocalDateTime(firstDayofMonth);
            const endTimestamp = this.toLocalDateTime(lastDayOfMonth)
                .plusDays(1)
                .minusSeconds(1);
            const monthYear = this.getMonthYearLabel(firstDayofMonth);
            return { monthYear, startTimestamp, endTimestamp };
        });
        this.memoizedGetYearsWithTimestamp = memoize((fromDate) => {
            const yearsWithTimestamp = [];
            for (let offsetYears = 1; offsetYears <= DateService.numberOfYears; offsetYears++) {
                const date = fromDate.minusYears(offsetYears);
                const year = this.memoizedGetYearTimestamp(date);
                yearsWithTimestamp.push(year);
            }
            return yearsWithTimestamp;
        });
        this.memoizedGetMonthsWithTimestamp = memoize((fromDate) => {
            const monthsWithTimestamp = [];
            for (let offsetMonths = 1; offsetMonths <= DateService.numberOfMonths; offsetMonths++) {
                const date = fromDate.minusMonths(offsetMonths);
                const month = this.memoizedGetMonthYearTimestamp(date);
                monthsWithTimestamp.push(month);
            }
            return monthsWithTimestamp;
        });
        /*
          No need to initialize directly here — the Observables will emit values.
          If you need the values immediately in the constructor, you can subscribe (with caution).
          However, ideally, components that *consume* these values should handle the subscription.
        */
    }
    getYearsWithTimestamp(fromDate) {
        return this.memoizedGetYearsWithTimestamp(fromDate);
    }
    getMonthsWithTimestamp(fromDate) {
        return this.memoizedGetMonthsWithTimestamp(fromDate);
    }
    toLocalDateTime(date) {
        return LocalDateTime.of(date.year(), date.month(), date.dayOfMonth(), 0, 0);
    }
    getMonthYearLabel(date) {
        return `${this.titleFormatter.transform(date.month().name())} ${date.year().toString()}`;
    }
    getYearTimestamp(date) {
        return this.memoizedGetYearTimestamp(date);
    }
    getMonthYearTimestamp(date) {
        return this.memoizedGetMonthYearTimestamp(date);
    }
}
DateService.numberOfYears = 2;
DateService.numberOfMonths = 12;
DateService.ngInjectableDef = i0.defineInjectable({ factory: function DateService_Factory() { return new DateService(i0.inject(i1.TitleCasePipe)); }, token: DateService, providedIn: "root" });
